// @flow

import { type DetailedBoard } from 'app/packages/pinterest-flow-types/board';
import { type SaveButtonOptions } from 'app/common/react/components/PinRep/components/PinRepPrimaryContent';
import { type Surface } from 'app/common/react/components/PinRep/core/PinRepConfiguration';
import { type Viewer } from 'app/packages/pinterest-flow-types/viewer';

// Developers can pass saveButtonOptions, but if not, getSaveButtonOptions will build
// one based on other data passed to PinRepPrimaryContent.
export function getSaveButtonOptions(viewerBoardCount: number = 0): SaveButtonOptions {
  if (viewerBoardCount < 2) {
    return { type: 'classic' };
  }

  return { type: 'inline' };
}

type BuildPrimaryContentPropsArgs = {|
  isDenseView: boolean,
  isGroupBoard: boolean,
  isOwnBoard: boolean,
  isOwnPin?: boolean,
  isQuickPromotable?: boolean,
  isRemovable?: boolean,
  saveButtonOptions?: SaveButtonOptions,
  surface: Surface,
  viewer: Viewer,
  viewerIsCollaborator: boolean,
  board?: DetailedBoard,
|};

export type BuildPrimaryContentPropsOutput = {|
  derivedSaveButtonOptions: SaveButtonOptions,
  showEditButton: boolean,
  showLikeButton: boolean, // Like/Reactions button
  supportsReactionBar: boolean, // whether the Reactions button should expand to a selection bar
|};

// This function takes _descriptive_ props and outputs _imperative_ props describing the PinRep PrimaryContent.
// It does not handle what specific pin data values we show. PrimaryContent can handle that.
export default function buildPrimaryContentProps({
  isDenseView,
  isGroupBoard,
  isOwnBoard,
  isOwnPin,
  isQuickPromotable,
  isRemovable,
  saveButtonOptions,
  surface,
  viewer,
  viewerIsCollaborator,
  board,
}: BuildPrimaryContentPropsArgs) {
  const viewerBoardCount = viewer.isAuth ? viewer.boardCount : undefined;

  let derivedSaveButtonOptions = saveButtonOptions || getSaveButtonOptions(viewerBoardCount);
  if (isQuickPromotable) {
    derivedSaveButtonOptions = { type: 'promote' };
  } else if (isOwnBoard && surface === 'HomefeedMoreIdeasTabs') {
    derivedSaveButtonOptions = { type: 'persistentOneClickSave' };
  } else if (
    viewerIsCollaborator ||
    isOwnBoard ||
    ['BrioCloseupRelatedProducts', 'UserProfilePinGrid'].includes(surface)
  ) {
    derivedSaveButtonOptions = { type: 'classic' };
  }

  // Never show a save button for "removable pins":
  //  promoted pins that typically involve copyright issues or are time-sensitive
  if (isRemovable) {
    derivedSaveButtonOptions = { type: 'none' };
  }

  const showEditButton =
    (isOwnBoard && !isGroupBoard) || (isOwnPin && surface === 'UserProfilePinGrid');
  const showLikeButton = viewerIsCollaborator && isGroupBoard;
  const supportsReactionBar = !isDenseView;

  return {
    derivedSaveButtonOptions,
    showEditButton,
    showLikeButton,
    supportsReactionBar,
    board,
  };
}
